// Copyright (c) 2026 Interpaste, all rights reserved
// @author: norelock <https://gitlab.com/mvvnlight>

import { ROOT, ORIGIN } from '../env'
import { Logger, PlayerSlot } from '../utils'
import { fillAbsClass } from '../styles'
import {
  YOUTUBE_IFRAME_API_URL,
  YOUTUBE_IFRAME_API_SCRIPT_REGEX,
  YOUTUBE_IFRAME_API_SCRIPT_SELECTOR,
} from '../constants'
import type { HostPayload, PlayerBridge, YTPlayer } from '../types'

export class YouTubePlayer implements PlayerBridge {
  private data: HostPayload | null = null
  private player: YTPlayer | null = null
  private finished = false
  private lastState = -1
  private apiPoll: ReturnType<typeof setInterval> | null = null
  private slot = new PlayerSlot('youtube', ROOT)
  private _postMessage: (msg: string) => void
  private _externalPause = false

  private logger: Logger = Logger.create('YTPlayer')

  constructor(postMessage: (msg: string) => void) {
    this._postMessage = postMessage
  }

  load(data: HostPayload): void {
    this.data = data
    this.finished = false
    this._externalPause = false

    if (this.player) {
      this.swap()
    } else {
      this.loadAPI()
    }
  }

  handleCommand(msg: string): boolean {
    const player = this.player

    if (msg === 'durationCheck') {
      if (player) this._postMessage(`durationCheck=${Math.round(player.getDuration())}`)
      return true
    }
    if (msg === 'unMute') {
      player?.unMute()
      return true
    }
    if (msg.startsWith('setPlaybackQuality=')) {
      player?.setPlaybackQuality(msg.split('=').pop()!)
      return true
    }
    if (msg.startsWith('stopVideo')) {
      player?.stopVideo()
      return true
    }
    if (msg.startsWith('setVolume=')) {
      const vol = parseInt(msg.split('=').pop()!, 10)
      if (!isNaN(vol)) player?.setVolume(vol)
      return true
    }
    if (msg.startsWith('seekTo=')) {
      const seconds = parseInt(msg.split('=').pop()!, 10)
      if (!isNaN(seconds)) {
        player?.seekTo(seconds, true)
        this.logger.debug('seekTo', seconds)
      }
      return true
    }
    if (msg === 'pauseVideo') {
      this._externalPause = true
      player?.pauseVideo()
      this.logger.debug('pauseVideo')
      return true
    }
    if (msg === 'playVideo') {
      this._externalPause = false
      player?.playVideo()
      this.logger.debug('playVideo')
      return true
    }

    return false
  }

  handleYouTubeEvent(parsed: Record<string, unknown>): boolean {
    if (typeof parsed.event === 'undefined') return false

    if (parsed.event === 'infoDelivery') {
      const info = parsed.info as Record<string, unknown> | undefined
      const progress = info?.progressState as Record<string, unknown> | undefined
      if (progress?.current != null) {
        this._postMessage(`currentTime=${progress.current}`)
      }
      return true
    }

    return false
  }

  destroy(): void {
    if (this.apiPoll !== null) {
      clearInterval(this.apiPoll)
      this.apiPoll = null
    }
    // YT player.destroy() removes the iframe it created; then remove the slot div too
    this.player?.destroy()
    this.player = null
    this.slot.destroy()
    window.onYouTubeIframeAPIReady = () => { }
    this.logger.debug('YT destroyed')
  }

  private loadAPI(): void {
    if (window.YT?.Player) {
      this.logger.debug('YT API already available, init player directly')
      this.initPlayer()
      return
    }

    window.onYouTubeIframeAPIReady = () => this.initPlayer()

    const scriptAlreadyPresent = !!document.querySelector(YOUTUBE_IFRAME_API_SCRIPT_SELECTOR)
      || Array.from(document.scripts).some((script) => YOUTUBE_IFRAME_API_SCRIPT_REGEX.test(script.src))

    if (scriptAlreadyPresent) {
      this.logger.debug('YT API script in DOM, waiting for ready callback')
      this.apiPoll = setInterval(() => {
        if (window.YT?.Player) {
          clearInterval(this.apiPoll!)
          this.apiPoll = null
          window.onYouTubeIframeAPIReady = () => { }
          this.initPlayer()
        }
      }, 50)
      return
    }

    const script = document.createElement('script')
    script.src = YOUTUBE_IFRAME_API_URL
    script.onload = () => this.logger.debug('YouTube IFrame API loaded')
    document.head.appendChild(script)
  }

  private initPlayer(): void {
    const data = this.data!

    this.slot.show()
    const div = this.slot.node({
      tag: 'div',
      key: 'yt-host',
      className: fillAbsClass,
    })
    div.replaceChildren()

    this.player = new window.YT.Player(div, {
      width: '100%',
      height: '100%',
      videoId: data.id,
      playerVars: this.buildPlayerVars(),
      events: {
        onReady: (e) => this.onReady(e),
        onStateChange: (e) => this.onStateChange(e),
        onError: (e) => this.onError(e),
      },
    })

    this.logger.debug('YT player created')
  }

  private buildPlayerVars(): Record<string, string | number> {
    const data = this.data!
    // origin must be the page that hosts this script
    return {
      html5: 1,
      autoplay: 1,
      mute: 1,
      controls: 0,
      disablekb: 1,
      enablejsapi: 1,
      playsinline: 1,
      rel: 0,
      showinfo: 0,
      iv_load_policy: 3,
      modestbranding: 1,
      cc_load_policy: 0,
      origin: ORIGIN,
      playerapiid: 'player',
      start: data.seek,
    }
  }

  private swap(): void {
    const data = this.data!
    const player = this.player!

    this.finished = false
    this.lastState = -1

    player.mute()
    player.loadVideoById({
      videoId: data.id,
      startSeconds: data.seek,
      suggestedQuality: data.quality ?? 'default',
    })

    setTimeout(() => {
      player.unMute()
      player.setVolume(data.volume)
      this._postMessage('playbackReady')
    }, 100)

    this.logger.debug('YT video swapped', JSON.stringify(data))
  }

  private onReady(event: { target: YTPlayer }): void {
    event.target.playVideo()
    setTimeout(() => {
      this.player!.unMute()
      this.player!.setVolume(this.data!.volume)
      this._postMessage('playbackReady')
    }, 100)
  }

  private onStateChange(event: { data: number }): void {
    const state = event.data
    if (state === this.lastState) return
    this.lastState = state

    const { PAUSED, ENDED } = window.YT.PlayerState
    const player = this.player!

    if (state === ENDED) {
      this.finished = true
      this._postMessage('playbackComplete')
    } else if (state === PAUSED && !this.finished) {
      if (!this._externalPause && player.getDuration() - player.getCurrentTime() > 2) {
        this._postMessage('playbackReady')
        setTimeout(() => player.playVideo(), 10)
      }
    }

    this.logger.debug('YT state', state)
  }

  private onError(event: { data: number }): void {
    this._postMessage(`onPlayerError=${event.data}`)
  }
}